import { ConfigProvider } from 'antd';
import React, { Suspense, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import {
  accessibilityActions,
  layoutActions,
  layoutSelectors,
  logoutTimeoutModalSelectors,
  useDispatch,
  userSelectors,
  useSelector
} from 'src/store';
import { ThemeProvider } from 'styled-components';

import AppRoutes from './AppRoutes';
import AccessibilityButton from './components/accessibilityButton';
import DownloadAppModal from './components/downloadApp';
import Loading from './components/Loading';
import LogoutModal from './components/logoutModal';
import i18n from './i18n';
import LocaleContext from './LocaleContext';
import { GlobalStyle } from './styles/globalStyle';
import { useTheme } from './styles/theme';
import { logOut } from './utils';

function App() {
  // TOOLS
  const dispatch = useDispatch();
  i18n.on('languageChanged', () => setLocale(i18n.language));
  const theme = useTheme();

  // STATE
  const [locale, setLocale] = useState(i18n.language);

  // SELECTORS
  const user = useSelector(userSelectors.selectUser);

  const closeDownloadAppModal = () => {
    dispatch(layoutActions.closeDownloadAppModal());
  };

  const getSideByLanguage = () => {
    const RTL_LANGUAGES = ['he', 'ar'];
    const isRtlLanguage = RTL_LANGUAGES.includes(locale);
    if (isRtlLanguage) {
      return 'rtl';
    }
    return 'ltr';
  };

  const changeFontSize = () => {
    dispatch(accessibilityActions.toggleFontSize());
  };

  const toggleTheme = () => {
    dispatch(accessibilityActions.toggleDarkMode());
  };
  const islogoutModalOpen = useSelector(
    logoutTimeoutModalSelectors.isModalopen
  );
  const isDownloadAppModalOpened = useSelector(
    layoutSelectors.selectIsDownloadAppModalOpened
  );

  console.log('Loading version 1.18.4.5');

  const hexToRgb = (hex: string) => {
    // Remove the leading '#' if present
    hex = hex.replace(/^#/, '');

    // If shorthand HEX (#FFF), expand it to full HEX (#FFFFFF)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char: any) => char + char)
        .join('');
    }

    // Extract red, green, and blue components
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // console.log(`${r}, ${g}, ${b}`);

    return `${r}, ${g}, ${b}`; // Return as a string
  };

  // Create a function to inject CSS variables dynamically
  const injectColors = () => {
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      // HEX COLORS
      const cssVariableName = `--${key}`; // Convert camelCase to kebab-case
      root.style.setProperty(cssVariableName, value);
      // RGB COLORS
      const rgb = hexToRgb(value);
      root.style.setProperty(`${cssVariableName}-rgb`, rgb);
    });
  };

  useEffect(() => {
    injectColors();
  }, []);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <ThemeProvider theme={theme}>
        <Suspense fallback={<Loading />}>
          <Helmet
            htmlAttributes={{
              lang: locale,
              dir: getSideByLanguage()
            }}
          />
          <ConfigProvider direction={getSideByLanguage()}>
            <LogoutModal
              isModalVisible={islogoutModalOpen}
              handleOk={() => logOut()}
              handleCancel={() => logOut()}
            />
            <AppRoutes />
            <AccessibilityButton
              toggleTheme={toggleTheme}
              changeFontSize={changeFontSize}
            />
            {user?.IsActive && (
              <DownloadAppModal
                closeModal={closeDownloadAppModal}
                visible={isDownloadAppModalOpened}
              />
            )}
            <GlobalStyle />
          </ConfigProvider>
        </Suspense>
      </ThemeProvider>
    </LocaleContext.Provider>
  );
}

export default App;
